// src/components/pages/portfolios/hooks/useSnapshotData.js
import { useState, useEffect, useMemo, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// 주차(Week) 계산 헬퍼
const getWeekString = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const year = d.getUTCFullYear();
    const weekNo = Math.ceil((((d - new Date(Date.UTC(year, 0, 1))) / 86400000) + 1) / 7);
    return `${year}-W${String(weekNo).padStart(2, '0')}`;
};

// =======================================================================
// 💡 추가: 캐시 유효성 검사 함수 (매일 오전 9시 KST 기준)
// =======================================================================
const isCacheValid = (timestamp) => {
    if (!timestamp) return false;
    const cacheTime = new Date(timestamp);
    const now = new Date();
    
    // 기준 시각 설정: UTC 00:00 (한국 시간 09:00)
    const lastUpdatePoint = new Date();
    lastUpdatePoint.setUTCHours(0, 0, 0, 0); 

    if (now < lastUpdatePoint) {
        lastUpdatePoint.setDate(lastUpdatePoint.getDate() - 1);
    }

    return cacheTime > lastUpdatePoint;
};

export function useSnapshotData(initialSortKey = 'ret_1w') {
  const today = new Date();
  const MIN_PERIOD = '2025-W52'; 
  const MAX_PERIOD = getWeekString(today); 

  const [currentPeriod, setCurrentPeriod] = useState(MAX_PERIOD);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: initialSortKey, direction: 'desc' });

  // 💡 데이터 페칭 로직을 useCallback으로 분리하고 캐싱 적용
  const fetchSnapshots = useCallback(async (period) => {
    // 캐시 키 생성: 명예의 전당은 주차(period)별로 고유해야 함
    const cacheKey = `hof_cache_${period}`;
    
    // 1. 캐시 확인
    const cachedDataStr = sessionStorage.getItem(cacheKey);
    if (cachedDataStr) {
        try {
            const cached = JSON.parse(cachedDataStr);
            if (isCacheValid(cached.timestamp)) {
                console.log(`[Cache Hit] Hall of Fame (${period})`);
                setItems(cached.rankings);
                setError(null);
                setLoading(false);
                return;
            }
        } catch (e) {
            sessionStorage.removeItem(cacheKey);
        }
    }

    // 2. 캐시가 없거나 만료된 경우 API 호출
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/portfolios/rankings/${period}`);
      if (!res.ok) throw new Error('Data not found');
      const data = await res.json();
      const rankings = data.rankings || [];
      
      setItems(rankings);
      setError(null);

      // 3. API 결과 캐시에 저장
      sessionStorage.setItem(cacheKey, JSON.stringify({
          rankings: rankings,
          timestamp: new Date().toISOString()
      }));
    } catch (err) {
      setItems([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // 주차가 변경될 때마다 실행
  useEffect(() => {
    fetchSnapshots(currentPeriod);
  }, [currentPeriod, fetchSnapshots]);

  // 정렬 로직 (기존 유지)
  const sortedItems = useMemo(() => {
    if (!items.length) return [];
    const sorted = [...items];
    const { key, direction } = sortConfig;
    const [metric, period] = key.split('_');

    sorted.sort((a, b) => {
      const valA = a.metrics?.[metric]?.[period] ?? -9999;
      const valB = b.metrics?.[metric]?.[period] ?? -9999;
      return direction === 'asc' ? valA - valB : valB - valA;
    });
    return sorted;
  }, [items, sortConfig]);

  // 주차 이동 핸들러 (기존 유지)
  const moveWeek = (offset) => {
    const [year, week] = currentPeriod.split('-W').map(Number);
    const date = new Date(year, 0, (week - 1) * 7 + 1 + (offset * 7)); 
    const nextPeriod = getWeekString(date);

    if (offset > 0 && nextPeriod > MAX_PERIOD) return;
    if (offset < 0 && nextPeriod < MIN_PERIOD) return;

    setCurrentPeriod(nextPeriod);
  };

  const requestSort = (sortKey) => {
    let direction = 'desc';
    if (sortKey.includes('mdd')) {
      direction = 'asc';
    }
    setSortConfig({ key: sortKey, direction: direction });
  };
  
  return {
    items: sortedItems,
    loading,
    error,
    currentPeriod,
    maxPeriod: MAX_PERIOD,
    minPeriod: MIN_PERIOD,
    sortConfig,
    requestSort,
    moveWeek
  };
}